// src/services/memorizationSuggestionsService.js
//
// Auto-promotion suggestions for the student `memorizedPortions` roster.
//
// A rub' that a student repeatedly recites at "ممتاز" level (hifz ≥ 85 +
// tajweed ≥ 70) over several distinct dates is a strong signal that they
// have actually memorised it — often the teacher simply forgot to mark
// it. This service scans the student's wird-rating history for such
// rub's and queues a *suggestion* (not a silent write) so a teacher can
// approve or dismiss before the roster changes.
//
// Collection: `memorizationSuggestions`
// Doc id:     `${studentId}__${portionId}` — deterministic so re-running
//             the detector on fresh evidence updates the same row.
//
// Doc shape:
//   tenantId, studentId, classId, portionId (1..240),
//   hizb, rubuu,
//   detectedAt: Timestamp,
//   evidence: [{ wirdRatingId, date, hifzScore, tajweedScore, kind }],
//   avgHifz, avgTajweed, passCount,
//   status: 'pending' | 'approved' | 'dismissed',
//   resolvedBy, resolvedAt
//
// The "prior vs at-school" distinction the user asked for lives on the
// student doc: `priorMemorizedPortions` is a snapshot of what was set at
// creation (pre-school hifz), `autoMemorizedPortions` is the subset of
// `memorizedPortions` that landed via approved suggestions. Anything in
// `memorizedPortions` outside both is implicitly manual/staff-taught.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  approveSuggestionCallable,
  dismissSuggestionCallable,
} from './callableFunctions';
import {
  upsertSuggestion as upsertSuggestionV2,
  listPendingSuggestionsForStudent as listPendingForStudentV2,
  listPendingSuggestionsForTenant as listPendingForTenantV2,
  approveSuggestion as approveSuggestionV2,
  dismissSuggestion as dismissSuggestionV2
} from './memorizationSuggestionsServiceV2';

const AUTH_BACKEND = process.env.REACT_APP_AUTH_BACKEND || 'firebase';

export const MEMORIZATION_SUGGESTIONS = 'memorizationSuggestions';

// Defaults — tuned conservatively so teachers don't get spammed.
export const DEFAULT_THRESHOLDS = Object.freeze({
  minPasses: 3,         // at least N wird ratings on the same rub'
  minDistinctDates: 2,  // spread across >= N different days
  minHifz: 85,          // hifz ≥ 85 on each passing rating
  minTajweed: 70,       // tajweed ≥ 70 on each passing rating
});

export const SUGGESTION_STATUS = Object.freeze({
  PENDING: 'pending',
  APPROVED: 'approved',
  DISMISSED: 'dismissed',
});

/** Convert (hizb, rubuu) to the linear 1..240 portion id. */
export const toPortionId = (hizb, rubuu) => {
  const h = Number(hizb);
  const r = Number(rubuu);
  if (!Number.isInteger(h) || h < 1 || h > 60) return null;
  if (!Number.isInteger(r) || r < 1 || r > 4) return null;
  return (h - 1) * 4 + r;
};

/** Deterministic doc id — one suggestion row per (student, portion). */
export const suggestionDocId = (studentId, portionId) =>
  `${studentId}__${portionId}`;

// ---------------------------------------------------------------------------
// Pure detection logic
// ---------------------------------------------------------------------------

/**
 * Scan a student's wirdRating history for rub's that cross the promotion
 * threshold AND are not already on their `memorizedPortions` list.
 *
 * @param {Object}   params
 * @param {Array}    params.wirdRatings        — full wirdRating docs for the student
 * @param {number[]} params.memorizedPortions  — portions already on the roster
 * @param {Object}   [params.thresholds]       — override thresholds
 * @returns {Array<{ portionId, hizb, rubuu, evidence, avgHifz, avgTajweed, passCount }>}
 */
export function detectMemorizationSuggestions({
  wirdRatings,
  memorizedPortions,
  thresholds = DEFAULT_THRESHOLDS,
}) {
  const t = { ...DEFAULT_THRESHOLDS, ...(thresholds || {}) };
  const already = new Set((memorizedPortions || []).map(Number));

  // Group passing ratings by portionId.
  const byPortion = new Map();
  for (const w of wirdRatings || []) {
    if (!w || !w.portion) continue;
    // Skip custom-verse ratings — there's no single rub' mapping.
    if (w.portion.mode !== 'rubuu') continue;
    const portionId = toPortionId(w.portion.hizb, w.portion.rubuu);
    if (!portionId) continue;
    if (already.has(portionId)) continue;
    const hifz = Number(w.hifzScore);
    const taj = Number(w.tajweedScore);
    if (!(hifz >= t.minHifz && taj >= t.minTajweed)) continue;

    let bucket = byPortion.get(portionId);
    if (!bucket) {
      bucket = {
        portionId,
        hizb: w.portion.hizb,
        rubuu: w.portion.rubuu,
        evidence: [],
      };
      byPortion.set(portionId, bucket);
    }
    bucket.evidence.push({
      wirdRatingId: w.id || null,
      date: w.date,
      hifzScore: hifz,
      tajweedScore: taj,
      kind: w.kind || null,
    });
  }

  const out = [];
  for (const bucket of byPortion.values()) {
    const { evidence } = bucket;
    if (evidence.length < t.minPasses) continue;
    const distinctDates = new Set(evidence.map((e) => e.date)).size;
    if (distinctDates < t.minDistinctDates) continue;

    const avgHifz = Math.round(
      evidence.reduce((a, b) => a + b.hifzScore, 0) / evidence.length
    );
    const avgTajweed = Math.round(
      evidence.reduce((a, b) => a + b.tajweedScore, 0) / evidence.length
    );

    out.push({
      portionId: bucket.portionId,
      hizb: bucket.hizb,
      rubuu: bucket.rubuu,
      evidence,
      avgHifz,
      avgTajweed,
      passCount: evidence.length,
    });
  }
  // Stable ordering: earliest rub' first.
  out.sort((a, b) => a.portionId - b.portionId);
  return out;
}

// ---------------------------------------------------------------------------
// Firestore layer
// ---------------------------------------------------------------------------

/**
 * Upsert a single suggestion row. Uses deterministic ids so re-running
 * detection on fresh evidence updates the same doc rather than piling
 * duplicates. Existing `approved`/`dismissed` rows are NOT reopened —
 * once resolved, the teacher's decision sticks.
 */
async function upsertSuggestionFirebase({
  tenantId,
  studentId,
  classId,
  detection,
}) {
  if (!tenantId) throw new Error('tenantId is required');
  if (!studentId) throw new Error('studentId is required');
  if (!detection || !detection.portionId) throw new Error('detection.portionId is required');

  const id = suggestionDocId(studentId, detection.portionId);
  const ref = doc(db, MEMORIZATION_SUGGESTIONS, id);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    const data = snap.data();
    if (data.status === SUGGESTION_STATUS.APPROVED
        || data.status === SUGGESTION_STATUS.DISMISSED) {
      return { id, skipped: true };
    }
    // Refresh the evidence / averages on the existing pending row.
    await updateDoc(ref, {
      evidence: detection.evidence,
      avgHifz: detection.avgHifz,
      avgTajweed: detection.avgTajweed,
      passCount: detection.passCount,
      detectedAt: serverTimestamp(),
    });
    return { id, updated: true };
  }

  await setDoc(ref, {
    tenantId,
    studentId,
    classId: classId || null,
    portionId: detection.portionId,
    hizb: detection.hizb,
    rubuu: detection.rubuu,
    evidence: detection.evidence,
    avgHifz: detection.avgHifz,
    avgTajweed: detection.avgTajweed,
    passCount: detection.passCount,
    status: SUGGESTION_STATUS.PENDING,
    detectedAt: serverTimestamp(),
    resolvedBy: null,
    resolvedAt: null,
  });
  return { id, created: true };
}

/**
 * List pending suggestions for a student. Sorted by portionId ascending
 * so the UI shows rub's in Qur'anic order.
 */
async function listPendingSuggestionsForStudentFirebase(studentId) {
  if (!studentId) return [];
  const q = query(
    collection(db, MEMORIZATION_SUGGESTIONS),
    where('studentId', '==', studentId),
    where('status', '==', SUGGESTION_STATUS.PENDING),
    orderBy('portionId', 'asc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Tenant-wide list of pending suggestions in a single Firestore query.
 * Used by DirectorDashboard so the moudir doesn't fan out N student
 * queries (the per-student count is derived in JS from this one list).
 *
 * Status is filtered client-side — the row count per tenant is small
 * (a few hundred at most) so this avoids needing a composite
 * (tenantId, status) index.
 */
async function listPendingSuggestionsForTenantFirebase(tenantId) {
  if (!tenantId) return [];
  const q = query(
    collection(db, MEMORIZATION_SUGGESTIONS),
    where('tenantId', '==', tenantId),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((s) => s.status === SUGGESTION_STATUS.PENDING);
}

/**
 * Approve a suggestion via the `approveSuggestion` callable Cloud
 * Function. The callable atomically flips the suggestion row, arrayUnions
 * the portion into students/{id}.{memorizedPortions, autoMemorizedPortions},
 * and writes a `students/{id}/memorizationEdits` audit row.
 */
async function approveSuggestionFirebase({ suggestionId, studentId, portionId }) {
  if (!suggestionId) throw new Error('suggestionId is required');
  if (!studentId) throw new Error('studentId is required');
  if (!portionId) throw new Error('portionId is required');
  await approveSuggestionCallable({ suggestionId, studentId, portionId });
}

async function dismissSuggestionFirebase(suggestionId, reason = null) {
  if (!suggestionId) throw new Error('suggestionId is required');
  await dismissSuggestionCallable({ suggestionId, reason });
}

// Phase 2.7 selectors — switch at module load.
export const upsertSuggestion = AUTH_BACKEND === 'api' ? upsertSuggestionV2 : upsertSuggestionFirebase;
export const listPendingSuggestionsForStudent = AUTH_BACKEND === 'api' ? listPendingForStudentV2 : listPendingSuggestionsForStudentFirebase;
export const listPendingSuggestionsForTenant = AUTH_BACKEND === 'api' ? listPendingForTenantV2 : listPendingSuggestionsForTenantFirebase;
export const approveSuggestion = AUTH_BACKEND === 'api' ? approveSuggestionV2 : approveSuggestionFirebase;
export const dismissSuggestion = AUTH_BACKEND === 'api' ? dismissSuggestionV2 : dismissSuggestionFirebase;

// Exposed for tests.
export const __test__ = {
  toPortionId,
  suggestionDocId,
};
